import { FC, useState } from 'react'
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Command,
  CommandItem,
  CommandList,
  Input,
} from '@wellesley/ui'
import 'types/wlsy'
import { User, api } from '@wellesley/openapi'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppsNavigate } from '@wellesley/router'
import useGuestFollow from './useGuestFollow'
import getMarkedText from 'helpers/getMarkedText'
import CommonDialog, {
  type CommonDialogProps,
} from 'components/modals/CommonDialog'
import { formSchema } from './schema'
import { getDisplayUsername } from '@wellesley/user'

type GuestFollowProps = CommonDialogProps & {
  user: User
}

const GuestFollowModal: FC<GuestFollowProps> = ({
  open,
  user,
  onCloseModal,
}) => {
  const handleSubmit = useGuestFollow({ user })
  const navigate = useAppsNavigate()
  const [canSearch, setCanSearch] = useState(true)

  const handleCreateAccount = () => {
    onCloseModal?.()
    navigate('/signin', { props: { redirectUrl: window.location.href } })
  }

  const form = useForm({
    shouldFocusError: false,
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: '',
    },
  })

  const [trigger, { data }] =
    api.endpoints.getApiV1InstanceDomains.useLazyQuery()

  const search = (query: string) => {
    if (!canSearch) return

    void trigger({
      limit: 20,
      domain: query,
      software: 'mastodon',
    })
  }

  const handleChange = (value: string) => {
    !canSearch && setCanSearch(true)
    form.setValue('url', value)
    search(value)
  }

  const canShowList = canSearch && !!form.getValues('url')

  return (
    <CommonDialog
      open={open}
      onCloseModal={onCloseModal}
      title={`You're not following ${getDisplayUsername(user.username)}`}
    >
      <Form {...form}>
        <form
          className="gap-xs md:mb-s flex flex-col md:flex-row md:items-end"
          autoFocus={false}
          onSubmit={(...args) => void form.handleSubmit(handleSubmit)(...args)}
        >
          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem className="mt-0 flex-1">
                <FormLabel className="mb-xxs block text-base">
                  Enter your server name to follow this user
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      data-testid="input"
                      placeholder="Server name"
                      {...field}
                      onChange={(e) => handleChange(e.target.value)}
                    />
                  </FormControl>
                  <Command data-testid="command">
                    <CommandList
                      data-testid="list"
                      className="mt-xxxs absolute z-50 w-full rounded-md bg-white shadow-md empty:hidden"
                    >
                      {canShowList &&
                        data?.map((item) => (
                          <CommandItem
                            className="w-full max-w-[unset] [&>div]:overflow-hidden [&>div]:text-ellipsis"
                            key={item}
                            onSelect={(v) => {
                              setCanSearch(false)
                              form.setValue('url', v)
                            }}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: getMarkedText(
                                  item,
                                  form.getValues('url'),
                                ),
                              }}
                            ></div>
                          </CommandItem>
                        ))}
                    </CommandList>
                  </Command>
                </div>
                <FormMessage className="md:absolute" />
              </FormItem>
            )}
          />
          <Button type="submit" variant="primary">
            Take me home
          </Button>
        </form>
      </Form>
      <div>
        <span className="text-base text-gray-500">
          Do not have an account yet?
        </span>
        <Button
          data-testid="join"
          variant="tertiary"
          className="ml-xxxs text-base text-blue-500"
          onClick={handleCreateAccount}
        >
          Join Wellsley
        </Button>
      </div>
    </CommonDialog>
  )
}

export default GuestFollowModal
